import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconCheck, IconChevronRight, IconCircleCheck, IconClock, IconDownload, IconInfoCircle, IconPackageImport, IconPlus, IconBuildingWarehouse, IconUserPlus, IconListDetails, IconAdjustmentsHorizontal, IconTags } from '@tabler/icons-react';

import {
  emptyFoundationsSignals,
  foundationsProgressPercent,
  isFoundationStepComplete,
  isFoundationsComplete,
  CANONICAL_PRODUCT_DETAIL_TYPES,
  CANONICAL_OPTION_GROUPS,
  CANONICAL_PRODUCT_TAG_GROUPS,
  ROLE_PACKS,
  SEEDED_ROLE_NAMES,
  grantLabelFr,
  productDetailTypeValueExamplesFr,
  type FoundationsSignals,
  type FoundationsStepId,
  type ProductDetailKindCode,
} from '@wapharma/domain';
import {
  Avatar,
  ScrollArea,
  ProximityScope,
  Button,
  ConfirmModalFooter,
  IconButton,
  InlineError,
  Modal,
  Text,
  Tooltip,
  WP_ICON_NAV,
  useOverlayFocusTrap,
  cx,
} from '@wapharma/design-system';
import { useGuide } from '../contexts/GuideContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { TOURS_ENABLED } from '../tours/flags';
import {
  capabilityPackDescriptionFr,
  capabilityPackLabelFr,
  userRoleDescriptionFr,
  userRoleLabelFr,
  userRolePacks,
} from '../constants/userRoleDisplay';
import { rolePersonaDataUri } from '../lib/rolePersonaAvatar';
import { RoleAccessCoverageCell } from './RoleAccessCoverageCell';
import { SetupWizardPathArt } from './SetupWizardPathArt';
import { SetupWizardTemplateMenu } from './SetupWizardTemplateMenu';
import {
  productDetailKindIcon,
  productDetailKindShortLabel,
} from './catalog/ProductDetailTypeCreateFields';
import { parseProductDetailKind } from '../lib/productFicheDetails';
import { canAccessInitialSetup, unwrapData } from '@wapharma/shared';
import { apiGet, apiPost } from '../api';
import { apiGetListSimple } from '../apiList';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { isSetupDismissed, setSetupDismissed } from '../lib/setupPrefs';
import {
  beginAwaitCategoriesImport,
  beginAwaitCustomRoleCreate,
  beginAwaitLocationCreate,
  beginAwaitMarkeCreate,
  beginAwaitMarkeImport,
  beginAwaitOptionGroupCreate,
  beginAwaitTagFamilyCreate,
  beginAwaitTagsBulkImport,
  beginAwaitProductDetailTypeCreate,
  beginAwaitStructureCreate,
  beginAwaitRolesBulkImport,
  beginAwaitStaffUserCreate,
  beginAwaitStaffUsersImport,
  getSetupWizardUiState,
  setSetupWizardUiState,
  subscribeCategoriesImport,
  subscribeCustomRoleCreate,
  subscribeLocationCreate,
  subscribeMarkeCreate,
  subscribeMarkeImport,
  subscribeOpenSetup,
  subscribeOptionGroupCreate,
  subscribeTagFamilyCreate,
  subscribeTagsBulkImport,
  subscribeProductDetailTypeCreate,
  subscribeProductTypesImport,
  subscribeStructureCreate,
  subscribeRolesBulkImport,
  subscribeSetupWizardUiState,
  subscribeStaffUserCreate,
  subscribeStaffUsersBulkImport,
} from '../lib/setupShell';
import { MarkeLogoThumb } from './MarkeLogoField';
import { StockAlertsSettingsSection, type StockAlertsSettingsHandle } from './StockAlertsSettingsSection';
import {
  OrganizationLegalIdentityForm,
  type OrganizationLegalIdentityHandle,
} from './OrganizationLegalIdentityForm';
import { downloadRolesImportTemplate } from './useRolesBulkImport';
import { downloadTaxonomyFacetImportTemplate } from './useTaxonomyFacetBulkImport';
import { downloadStructureImportTemplate } from './useStructureBulkImport';
import { downloadUsersImportTemplate } from './useUsersBulkImport';
import { UserRolePill } from './UserRolePill';
import { formatUserFullName, type UserRow } from '../pages/users/usersPageHelpers';
import { ADMIN_USERS, ADMIN_PRODUCTS } from '../constants/adminNavLabels';
import type { CatRow, ProductTypeRow } from '../pages/taxonomy/taxonomyShared';
import { ProductTypeTaxonomyGraph } from './ProductTypeTaxonomyGraph';
import dockStyles from './OnboardingDock.module.css';
import { StepTravel } from './StepTravel';
import styles from './SetupWizard.module.css';

const SETUP_HEIGHT_VAR = '--wp-setup-dock-height';
const SETUP_PAD_VAR = '--wp-setup-dock-pad';

function publishSetupMetrics(sheet: HTMLElement): void {
  const fullH = Math.ceil(sheet.getBoundingClientRect().height);
  const strip = sheet.querySelector('[data-wp-dock-strip]');
  const stripH = strip
    ? Math.ceil(strip.getBoundingClientRect().height)
    : fullH;
  document.documentElement.style.setProperty(SETUP_HEIGHT_VAR, `${fullH}px`);
  document.documentElement.style.setProperty(SETUP_PAD_VAR, `${stripH}px`);
}

function RoleCapabilityTip({
  roleName,
  packs: packsProp,
  grants: grantsProp,
}: {
  roleName: string;
  packs?: readonly string[];
  /** Custom roles store domain grants; packs are often empty. */
  grants?: readonly string[];
}) {
  const packs = userRolePacks(roleName, packsProp);
  const useGrants = grantsProp !== undefined && grantsProp.length > 0;
  const desc = useGrants
    ? 'Capacités fines par domaine (rôle personnalisé).'
    : userRoleDescriptionFr(roleName, packs);
  return (
    <div className={styles.roleTipCard} role="presentation">
      <Text size="sm" fw={600} className={styles.roleTipTitle}>
        {userRoleLabelFr(roleName)}
      </Text>
      {desc ? (
        <Text size="xs" c="dimmed" className={styles.roleTipDesc}>
          {desc}
        </Text>
      ) : null}
      <RoleAccessCoverageCell packs={packs} grants={useGrants ? grantsProp : undefined} />
      {useGrants ? (
        <ul className={styles.roleTipPacks}>
          {[...grantsProp].sort().map((grant) => (
            <li key={grant}>{grantLabelFr(grant)}</li>
          ))}
        </ul>
      ) : packs.length > 0 ? (
        <ul className={styles.roleTipPacks}>
          {packs.map((pack) => (
            <li key={pack}>
              <strong>{capabilityPackLabelFr(pack)} :</strong> {capabilityPackDescriptionFr(pack)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ProductDetailTypeExamplesTip({
  name,
  code,
  kind,
}: {
  name: string;
  code: string;
  kind: ProductDetailKindCode;
}) {
  const tip = productDetailTypeValueExamplesFr(code, kind);
  const kindLabel = productDetailKindShortLabel(kind);
  return (
    <div className={styles.roleTipCard} role="presentation">
      <Text size="sm" fw={600} className={styles.roleTipTitle}>
        {name}
      </Text>
      <Text size="xs" c="dimmed" className={styles.roleTipDesc}>
        {tip.blurb} · saisie {kindLabel.toLowerCase()}
      </Text>
      <ul className={styles.roleTipPacks}>
        {tip.examples.map((ex) => (
          <li key={ex}>{ex}</li>
        ))}
      </ul>
    </div>
  );
}

function OptionGroupExamplesTip({
  name,
  blurb,
  examples,
}: {
  name: string;
  blurb: string;
  examples: readonly string[];
}) {
  return (
    <div className={styles.roleTipCard} role="presentation">
      <Text size="sm" fw={600} className={styles.roleTipTitle}>
        {name}
      </Text>
      <Text size="xs" c="dimmed" className={styles.roleTipDesc}>
        {blurb}
      </Text>
      {examples.length > 0 ? (
        <ul className={styles.roleTipPacks}>
          {examples.map((ex) => (
            <li key={ex}>{ex}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Scrollable role tag grid — mask fade via `.wp-scroll-fade` on scroller. */
function RoleTagGridShell({
  className,
  'aria-label': ariaLabel,
  children,
}: {
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
}) {
  return (
    <ScrollArea
      className={styles.roleTagGridShell}
      viewportClassName={[styles.roleTagGridViewport, className].filter(Boolean).join(' ')}
      fade="y"
      maxHeight="100%"
      viewportProps={{ role: 'list', 'aria-label': ariaLabel }}
    >
      {children}
    </ScrollArea>
  );
}

/** Team role rows — same mist scroll + cross-fade mask. */
function TeamRoleListShell({
  className,
  'aria-label': ariaLabel,
  children,
}: {
  className?: string;
  'aria-label'?: string;
  children: ReactNode;
}) {
  return (
    <ScrollArea
      className={styles.teamRoleListShell}
      viewportClassName={[styles.teamRoleListViewport, className].filter(Boolean).join(' ')}
      fade="y"
      maxHeight="100%"
      viewportProps={{ 'aria-label': ariaLabel }}
    >
      {children}
    </ScrollArea>
  );
}

const STEPS: { id: FoundationsStepId; label: string }[] = [
  { id: 'org_identity', label: 'Société' },
  { id: 'roles', label: 'Rôles' },
  { id: 'team', label: 'Équipe' },
  { id: 'markes', label: 'Marques' },
  { id: 'structure', label: 'Structure' },
  { id: 'fiches', label: 'Fiches' },
  { id: 'options', label: 'Variantes' },
  { id: 'tags', label: 'Tags' },
  { id: 'locations', label: 'Emplacements' },
  { id: 'pref_low_stock', label: 'Stock bas' },
  { id: 'pref_near_expiry', label: 'Péremption' },
];

function setupStepIndex(id: FoundationsStepId): number {
  return STEPS.findIndex((s) => s.id === id);
}

/** Suggested locations — same names as demo seed warehouse. */
const LOCATION_SEED_MODELS = [
  {
    type: 'WAREHOUSE' as const,
    name: 'Entrepôt principal',
    label: 'Entrepôt',
  },
  {
    type: 'SHOWROOM_OUTLET' as const,
    name: 'Point de vente',
    label: 'Point de vente',
  },
];

function foundationsFromPayload(
  raw: FoundationsSignals | null | undefined,
): FoundationsSignals {
  if (!raw) return emptyFoundationsSignals();
  return emptyFoundationsSignals(raw);
}

export function SetupWizard() {
  const { user, mustChangePassword } = useAuth();
  const guide = useGuide();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const sheetRef = useRef<HTMLElement | null>(null);
  const stockBasRef = useRef<StockAlertsSettingsHandle>(null);
  const orgIdentityRef = useRef<OrganizationLegalIdentityHandle>(null);
  const peremptionRef = useRef<StockAlertsSettingsHandle>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  useOverlayFocusTrap(open && expanded, sheetRef);
  const [collapseInstant, setCollapseInstant] = useState(false);
  const [awaitingRoleCreate, setAwaitingRoleCreate] = useState(false);
  const [awaitingRoleImport, setAwaitingRoleImport] = useState(false);
  const [awaitingStaffUser, setAwaitingStaffUser] = useState(false);
  const [awaitingStaffUsersImport, setAwaitingStaffUsersImport] = useState(false);
  const [awaitingMarkeCreate, setAwaitingMarkeCreate] = useState(false);
  const [awaitingMarkeImport, setAwaitingMarkeImport] = useState(false);
  const [awaitingProductTypesImport, setAwaitingProductTypesImport] = useState(false);
  const [awaitingStructureCreate, setAwaitingStructureCreate] = useState(false);
  const [awaitingCategoriesImport, setAwaitingCategoriesImport] = useState(false);
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);
  const [rolesImportDone, setRolesImportDone] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [autoTried, setAutoTried] = useState(false);
  /** After deep-link: reopen sheet (not dismissed — only paused for navigation). */
  const [reopenAfterNav, setReopenAfterNav] = useState(false);
  const [roleSeedBusy, setRoleSeedBusy] = useState(false);
  const [roleSeedErr, setRoleSeedErr] = useState<string | null>(null);
  const [presentRoleNames, setPresentRoleNames] = useState<Set<string>>(() => new Set());
  const [customRoles, setCustomRoles] = useState<
    Array<{ id: string; name: string; packs: string[]; grants: string[] }>
  >([]);
  const [setupMarkes, setSetupMarkes] = useState<
    Array<{ id: string; name: string; logoUrl: string | null }>
  >([]);
  const [setupProductTypes, setSetupProductTypes] = useState<ProductTypeRow[]>([]);
  const [setupCategories, setSetupCategories] = useState<CatRow[]>([]);
  const [setupSelectedTypeCode, setSetupSelectedTypeCode] = useState('');
  const [taxonomySeedModels, setTaxonomySeedModels] = useState<{
    productTypes: Array<{ code: string; label: string; present: boolean }>;
    rootCategories: Array<{ slug: string; name: string; present: boolean }>;
    complete: boolean;
    preview: {
      productTypes: ProductTypeRow[];
      categories: CatRow[];
    };
  } | null>(null);
  const [taxonomySeedBusy, setTaxonomySeedBusy] = useState(false);
  const [taxonomySeedErr, setTaxonomySeedErr] = useState<string | null>(null);
  const [seedPreviewTypeCode, setSeedPreviewTypeCode] = useState('');
  const [setupLocations, setSetupLocations] = useState<
    Array<{ id: string; name: string; type: string | null }>
  >([]);
  const [locationSeedBusy, setLocationSeedBusy] = useState(false);
  const [locationSeedErr, setLocationSeedErr] = useState<string | null>(null);
  const [awaitingLocationCreate, setAwaitingLocationCreate] = useState(false);
  const [setupProductDetailTypes, setSetupProductDetailTypes] = useState<
    Array<{ id: string; code: string; name: string; kind: ProductDetailKindCode }>
  >([]);
  const [ficheSeedBusy, setFicheSeedBusy] = useState(false);
  const [ficheSeedErr, setFicheSeedErr] = useState<string | null>(null);
  const [awaitingProductDetailTypeCreate, setAwaitingProductDetailTypeCreate] = useState(false);
  const [setupOptionGroups, setSetupOptionGroups] = useState<
    Array<{ id: string; code: string; name: string; valueCount: number }>
  >([]);
  const [optionSeedBusy, setOptionSeedBusy] = useState(false);
  const [optionSeedErr, setOptionSeedErr] = useState<string | null>(null);
  const [awaitingOptionGroupCreate, setAwaitingOptionGroupCreate] = useState(false);
  const [setupTagFamilies, setSetupTagFamilies] = useState<
    Array<{ id: string; code: string; name: string; tagCount: number }>
  >([]);
  const [tagSeedBusy, setTagSeedBusy] = useState(false);
  const [tagSeedErr, setTagSeedErr] = useState<string | null>(null);
  const [awaitingTagFamilyCreate, setAwaitingTagFamilyCreate] = useState(false);
  const [awaitingTagsImport, setAwaitingTagsImport] = useState(false);

  const refreshReadiness = guide.refreshReadiness;
  const offerParcoursCollapsed = guide.offerParcoursCollapsed;
  const closeGuide = guide.closeGuide;
  const readinessFoundations = guide.readiness?.foundations;
  const readinessComplete = guide.readiness?.foundationsComplete;

  const foundations = foundationsFromPayload(readinessFoundations ?? null);
  const complete = readinessComplete ?? isFoundationsComplete(foundations);
  const pct =
    guide.readiness?.foundationsProgressPercent ?? foundationsProgressPercent(foundations);

  const loadPresentRoles = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        name: string;
        seeded?: boolean;
        packs?: string[];
        grants?: string[];
      }>('/admin/roles', token);
      const names = new Set<string>();
      const custom: Array<{ id: string; name: string; packs: string[]; grants: string[] }> = [];
      for (const r of rows) {
        if (r.name in ROLE_PACKS) {
          names.add(r.name);
          continue;
        }
        custom.push({
          id: r.id,
          name: r.name,
          packs: r.packs ?? [],
          grants: r.grants ?? [],
        });
      }
      setPresentRoleNames(names);
      setCustomRoles(custom);
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupMarkes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        name: string;
        logoUrl?: string | null;
      }>('/catalog/admin/markes', token);
      setSetupMarkes(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          logoUrl: r.logoUrl ?? null,
        })),
      );
    } catch {
      /* count from readiness still drives Continuer */
    }
  }, []);

  const loadSetupProductTypes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<ProductTypeRow>('/catalog/admin/product-types', token);
      setSetupProductTypes(
        rows.map((r) => ({
          ...r,
          roots: Array.isArray(r.roots) ? r.roots : [],
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupCategories = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<CatRow>('/catalog/admin/categories', token);
      setSetupCategories(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          parentId: r.parentId ?? null,
          imageKey: r.imageKey ?? null,
          imageUrl: r.imageUrl ?? null,
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupLocations = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{ id: string; name: string; type?: string | null }>(
        '/inventory/locations',
        token,
      );
      setSetupLocations(
        rows.map((r) => ({ id: r.id, name: r.name, type: r.type ?? null })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupProductDetailTypes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{ id: string; code: string; name: string; kind?: string }>(
        '/catalog/admin/product-detail-types',
        token,
      );
      setSetupProductDetailTypes(
        rows.map((r) => ({
          id: r.id,
          code: r.code,
          name: r.name,
          kind: parseProductDetailKind(r.kind),
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupOptionGroups = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        code: string;
        name: string;
        values?: Array<{ id: string }>;
      }>('/catalog/admin/options/groups', token);
      setSetupOptionGroups(
        rows.map((r) => ({
          id: r.id,
          code: r.code,
          name: r.name,
          valueCount: Array.isArray(r.values) ? r.values.length : 0,
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadSetupTagFamilies = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const rows = await apiGetListSimple<{
        id: string;
        code: string;
        name: string;
        tags?: Array<{ id: string }>;
      }>('/catalog/admin/tags/families', token);
      setSetupTagFamilies(
        rows.map((r) => ({
          id: r.id,
          code: r.code,
          name: r.name,
          tagCount: Array.isArray(r.tags) ? r.tags.length : 0,
        })),
      );
    } catch {
      /* readiness count still drives Continuer */
    }
  }, []);

  const loadTaxonomySeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      type SeedModelsBody = {
        productTypes: Array<{ code: string; label: string; present: boolean }>;
        rootCategories: Array<{ slug: string; name: string; present: boolean }>;
        complete: boolean;
        preview?: {
          productTypes: ProductTypeRow[];
          categories: CatRow[];
        };
      };
      const raw = await apiGet<{ data: SeedModelsBody } | SeedModelsBody>(
        '/catalog/admin/taxonomy/seed-models',
        token,
      );
      const body = unwrapData(raw) as SeedModelsBody;
      setTaxonomySeedModels({
        productTypes: body.productTypes ?? [],
        rootCategories: body.rootCategories ?? [],
        complete: Boolean(body.complete),
        preview: {
          productTypes: (body.preview?.productTypes ?? []).map((r) => ({
            ...r,
            imageUrl: r.imageUrl ?? null,
            roots: Array.isArray(r.roots) ? r.roots : [],
          })),
          categories: (body.preview?.categories ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            slug: r.slug,
            parentId: r.parentId ?? null,
            imageUrl: r.imageUrl ?? null,
          })),
        },
      });
    } catch {
      /* optional — create/import still work */
    }
  }, []);

  const downloadFacetTemplate = useCallback(
    (facet: 'markes' | 'product-types' | 'categories', format: 'xlsx' | 'csv') => {
      const token = getToken();
      if (!token) return;
      void downloadTaxonomyFacetImportTemplate(token, facet, format);
    },
    [],
  );

  const downloadStructureTemplate = useCallback(() => {
    const token = getToken();
    if (!token) return;
    void downloadStructureImportTemplate(token);
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadPresentRoles();
  }, [open, loadPresentRoles, foundations.seededRoleCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupMarkes();
  }, [open, loadSetupMarkes, foundations.markeCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupProductTypes();
  }, [open, loadSetupProductTypes, foundations.productTypeCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupCategories();
  }, [open, loadSetupCategories, foundations.categoryCount]);

  useEffect(() => {
    if (!open) return;
    void loadTaxonomySeedModels();
  }, [
    open,
    loadTaxonomySeedModels,
    foundations.productTypeCount,
    foundations.categoryCount,
  ]);

  useEffect(() => {
    if (!open) return;
    void loadSetupLocations();
  }, [open, loadSetupLocations, foundations.locationCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupProductDetailTypes();
  }, [open, loadSetupProductDetailTypes, foundations.productDetailTypeCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupOptionGroups();
  }, [open, loadSetupOptionGroups, foundations.optionGroupWithValuesCount]);

  useEffect(() => {
    if (!open) return;
    void loadSetupTagFamilies();
  }, [open, loadSetupTagFamilies, foundations.tagFamilyWithValuesCount]);

  const locationSeedModels = useMemo(() => {
    const byType = new Set(
      setupLocations.map((l) => l.type).filter((t): t is string => Boolean(t)),
    );
    return LOCATION_SEED_MODELS.map((m) => ({
      ...m,
      present: byType.has(m.type),
    }));
  }, [setupLocations]);

  const locationSeedComplete = locationSeedModels.every((m) => m.present);

  const ficheSeedModels = useMemo(() => {
    const byCode = new Set(setupProductDetailTypes.map((t) => t.code));
    return CANONICAL_PRODUCT_DETAIL_TYPES.map((m) => ({
      code: m.code,
      name: m.name,
      kind: m.kind,
      present: byCode.has(m.code),
    }));
  }, [setupProductDetailTypes]);

  const ficheSeedComplete = ficheSeedModels.every((m) => m.present);

  const optionSeedModels = useMemo(() => {
    const byCode = new Map(setupOptionGroups.map((g) => [g.code, g]));
    return CANONICAL_OPTION_GROUPS.map((m) => {
      const existing = byCode.get(m.code);
      return {
        code: m.code,
        name: m.name,
        present: Boolean(existing && existing.valueCount > 0),
        valueLabels: m.values.map((v) => v.label),
      };
    });
  }, [setupOptionGroups]);

  const optionSeedComplete = optionSeedModels.every((m) => m.present);

  const tagSeedModels = useMemo(() => {
    const byCode = new Map(setupTagFamilies.map((g) => [g.code, g]));
    return CANONICAL_PRODUCT_TAG_GROUPS.map((m) => {
      const existing = byCode.get(m.code);
      return {
        code: m.code,
        name: m.name,
        present: Boolean(existing && existing.tagCount > 0),
        valueLabels: m.tags.map((t) => t.label),
      };
    });
  }, [setupTagFamilies]);

  const tagSeedComplete = tagSeedModels.every((m) => m.present);

  const setupSortedProductTypes = useMemo(() => {
    return [...setupProductTypes].sort((a, b) => {
      const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      if (o !== 0) return o;
      return (a.label ?? '').localeCompare(b.label ?? '', 'fr');
    });
  }, [setupProductTypes]);

  const hasSetupTaxonomyTree =
    setupSortedProductTypes.length > 0 || setupCategories.length > 0;

  useEffect(() => {
    if (setupSortedProductTypes.length === 0) {
      if (setupSelectedTypeCode) setSetupSelectedTypeCode('');
      return;
    }
    if (!setupSortedProductTypes.some((t) => t.code === setupSelectedTypeCode)) {
      setSetupSelectedTypeCode(setupSortedProductTypes[0]!.code);
    }
  }, [setupSortedProductTypes, setupSelectedTypeCode]);

  const reloadSetupTaxonomy = useCallback(async () => {
    await Promise.all([loadSetupProductTypes(), loadSetupCategories(), loadTaxonomySeedModels()]);
  }, [loadSetupProductTypes, loadSetupCategories, loadTaxonomySeedModels]);

  useEffect(() => {
    const types = taxonomySeedModels?.preview.productTypes ?? [];
    if (types.length === 0) {
      if (seedPreviewTypeCode) setSeedPreviewTypeCode('');
      return;
    }
    if (!types.some((t) => t.code === seedPreviewTypeCode)) {
      setSeedPreviewTypeCode(types[0]!.code);
    }
  }, [taxonomySeedModels, seedPreviewTypeCode]);

  const missingRoleTemplates = useMemo(
    () => SEEDED_ROLE_NAMES.filter((name) => !presentRoleNames.has(name)),
    [presentRoleNames],
  );

  const installRoleTemplates = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setRoleSeedErr('Session requise.');
      return;
    }
    setRoleSeedBusy(true);
    setRoleSeedErr(null);
    try {
      type EnsureBody = {
        created: string[];
        restored: string[];
        alreadyPresent: string[];
        roles: { name: string }[];
      };
      const raw = await apiPost<{ data: EnsureBody } | EnsureBody>(
        '/admin/roles/ensure-system',
        {},
        token,
      );
      const body = unwrapData(raw) as EnsureBody;
      const names = new Set<string>();
      for (const r of body.roles ?? []) {
        if (r.name in ROLE_PACKS) names.add(r.name);
      }
      for (const n of [
        ...(body.created ?? []),
        ...(body.restored ?? []),
        ...(body.alreadyPresent ?? []),
      ]) {
        names.add(n);
      }
      setPresentRoleNames(names.size > 0 ? names : new Set(SEEDED_ROLE_NAMES));
      await refreshReadiness();
      await loadPresentRoles();
    } catch (e: unknown) {
      setRoleSeedErr(getDisplayError(e));
    } finally {
      setRoleSeedBusy(false);
    }
  }, [refreshReadiness, loadPresentRoles]);

  const cycleTaxonomySeedPhoto = useCallback(async (seedKey: string): Promise<string | null> => {
    const token = getToken();
    if (!token || !seedKey.trim()) return null;
    try {
      const raw = await apiPost<
        | { data: { seedKey: string; imageUrl: string; index: number; total: number } }
        | { seedKey: string; imageUrl: string; index: number; total: number }
      >('/catalog/admin/taxonomy/seed-models/next-photo', { seedKey }, token);
      const body = unwrapData(raw) as {
        seedKey: string;
        imageUrl: string;
        index: number;
        total: number;
      };
      const imageUrl = body.imageUrl ?? null;
      if (!imageUrl) return null;
      setTaxonomySeedModels((prev) => {
        if (!prev) return prev;
        const key = body.seedKey || seedKey;
        return {
          ...prev,
          preview: {
            productTypes: prev.preview.productTypes.map((t) =>
              t.code === key ? { ...t, imageUrl } : t,
            ),
            categories: prev.preview.categories.map((c) =>
              c.slug === key ? { ...c, imageUrl } : c,
            ),
          },
        };
      });
      return imageUrl;
    } catch (e: unknown) {
      setTaxonomySeedErr(getDisplayError(e));
      return null;
    }
  }, []);

  const installTaxonomySeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTaxonomySeedErr('Session requise.');
      return;
    }
    setTaxonomySeedBusy(true);
    setTaxonomySeedErr(null);
    try {
      type SeedModelsBody = {
        productTypes: Array<{ code: string; label: string; present: boolean }>;
        rootCategories: Array<{ slug: string; name: string; present: boolean }>;
        complete: boolean;
        preview?: {
          productTypes: ProductTypeRow[];
          categories: CatRow[];
        };
      };
      const raw = await apiPost<{ data: SeedModelsBody } | SeedModelsBody>(
        '/catalog/admin/taxonomy/ensure-seed',
        {},
        token,
      );
      const body = unwrapData(raw) as SeedModelsBody;
      setTaxonomySeedModels({
        productTypes: body.productTypes ?? [],
        rootCategories: body.rootCategories ?? [],
        complete: Boolean(body.complete),
        preview: {
          productTypes: (body.preview?.productTypes ?? []).map((r) => ({
            ...r,
            imageUrl: r.imageUrl ?? null,
            roots: Array.isArray(r.roots) ? r.roots : [],
          })),
          categories: (body.preview?.categories ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            slug: r.slug,
            parentId: r.parentId ?? null,
            imageUrl: r.imageUrl ?? null,
          })),
        },
      });
      await refreshReadiness();
      await Promise.all([loadSetupProductTypes(), loadSetupCategories()]);
    } catch (e: unknown) {
      setTaxonomySeedErr(getDisplayError(e));
    } finally {
      setTaxonomySeedBusy(false);
    }
  }, [refreshReadiness, loadSetupProductTypes, loadSetupCategories]);

  const installLocationSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLocationSeedErr('Session requise.');
      return;
    }
    setLocationSeedBusy(true);
    setLocationSeedErr(null);
    try {
      const existing = await apiGetListSimple<{ id: string; type?: string | null }>(
        '/inventory/locations',
        token,
      );
      const haveType = new Set(
        existing.map((r) => r.type).filter((t): t is string => Boolean(t)),
      );
      for (const model of LOCATION_SEED_MODELS) {
        if (haveType.has(model.type)) continue;
        await apiPost('/inventory/locations', { name: model.name, type: model.type }, token);
        haveType.add(model.type);
      }
      await refreshReadiness();
      await loadSetupLocations();
    } catch (e: unknown) {
      setLocationSeedErr(getDisplayError(e));
    } finally {
      setLocationSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupLocations]);

  const installFicheSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setFicheSeedErr('Session requise.');
      return;
    }
    setFicheSeedBusy(true);
    setFicheSeedErr(null);
    try {
      await apiPost('/catalog/admin/product-detail-types/ensure-seed', {}, token);
      await refreshReadiness();
      await loadSetupProductDetailTypes();
    } catch (e: unknown) {
      setFicheSeedErr(getDisplayError(e));
    } finally {
      setFicheSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupProductDetailTypes]);

  const installOptionSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setOptionSeedErr('Session requise.');
      return;
    }
    setOptionSeedBusy(true);
    setOptionSeedErr(null);
    try {
      await apiPost('/catalog/admin/options/groups/ensure-seed', {}, token);
      await refreshReadiness();
      await loadSetupOptionGroups();
    } catch (e: unknown) {
      setOptionSeedErr(getDisplayError(e));
    } finally {
      setOptionSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupOptionGroups]);

  const installTagSeedModels = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTagSeedErr('Session requise.');
      return;
    }
    setTagSeedBusy(true);
    setTagSeedErr(null);
    try {
      await apiPost('/catalog/admin/tags/ensure-seed', {}, token);
      await refreshReadiness();
      await loadSetupTagFamilies();
    } catch (e: unknown) {
      setTagSeedErr(getDisplayError(e));
    } finally {
      setTagSeedBusy(false);
    }
  }, [refreshReadiness, loadSetupTagFamilies]);

  const refresh = useCallback(() => {
    void refreshReadiness();
  }, [refreshReadiness]);

  const openWizard = useCallback(
    (opts?: { step?: number }) => {
      if (
        !canAccessInitialSetup({
          roles: user?.roles,
          bootstrapAdmin: user?.bootstrapAdmin,
          foundationsComplete: complete,
        })
      ) {
        return;
      }
      closeGuide();
      setSetupDismissed(user?.id, false);
      setReopenAfterNav(false);
      if (opts?.step != null) setStepIndex(opts.step);
      setOpen(true);
      setExpanded(true);
      void refreshReadiness();
    },
    [closeGuide, complete, refreshReadiness, user?.bootstrapAdmin, user?.id, user?.roles],
  );

  const collapsePanel = useCallback(() => {
    setExpanded(false);
  }, []);

  const expandPanel = useCallback(() => {
    closeGuide();
    setExpanded(true);
  }, [closeGuide]);

  /** Only explicit exit before foundations complete. */
  const dismissForLater = useCallback(() => {
    setDismissConfirmOpen(false);
    setSetupDismissed(user?.id, true);
    setReopenAfterNav(false);
    setOpen(false);
    setExpanded(false);
  }, [user?.id]);

  const requestDismissForLater = useCallback(() => {
    setDismissConfirmOpen(true);
  }, []);

  useEffect(() => {
    if (!open || !user) return;
    // Role/bootstrap gate only. Do not pass foundationsComplete here —
    // when hard foundations flip true mid-wizard, soft steps (Stock bas,
    // Péremption) + Terminé must still run. Close via Terminer / Plus tard.
    if (
      canAccessInitialSetup({
        roles: user.roles,
        bootstrapAdmin: user.bootstrapAdmin,
        foundationsComplete: false,
      })
    ) {
      return;
    }
    setOpen(false);
    setExpanded(false);
  }, [open, user]);

  useEffect(() => {
    setSetupWizardUiState({
      visible: open,
      expanded: open && expanded,
      collapseInstant: false,
    });
  }, [open, expanded]);

  useEffect(() => {
    return () => setSetupWizardUiState({ visible: false, expanded: false, collapseInstant: false });
  }, []);

  useEffect(() => {
    return subscribeSetupWizardUiState(() => {
      const s = getSetupWizardUiState();
      if (!s.visible) return;
      if (!s.expanded) {
        if (s.collapseInstant) setCollapseInstant(true);
        setExpanded(false);
      }
    });
  }, []);

  useEffect(() => {
    if (!collapseInstant) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setCollapseInstant(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [collapseInstant, expanded]);

  useEffect(() => {
    return subscribeOpenSetup((opts) => openWizard(opts));
  }, [openWizard]);

  useEffect(() => {
    if (!open) {
      document.documentElement.style.removeProperty(SETUP_HEIGHT_VAR);
      document.documentElement.style.removeProperty(SETUP_PAD_VAR);
      return;
    }
    const el = sheetRef.current;
    if (!el) return;
    const publish = () => publishSetupMetrics(el);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty(SETUP_HEIGHT_VAR);
      document.documentElement.style.removeProperty(SETUP_PAD_VAR);
    };
  }, [open, expanded]);

  useEffect(() => {
    if (!user || mustChangePassword || autoTried) return;
    if (
      !canAccessInitialSetup({
        roles: user.roles,
        bootstrapAdmin: user.bootstrapAdmin,
        foundationsComplete: false,
      })
    ) {
      setAutoTried(true);
      return;
    }
    if (isSetupDismissed(user.id)) {
      setAutoTried(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const payload = await refreshReadiness();
      if (cancelled) return;
      setAutoTried(true);
      if (!payload) return;
      if (payload.foundationsComplete) return;
      if (isSetupDismissed(user.id)) return;
      if (
        !canAccessInitialSetup({
          roles: user.roles,
          bootstrapAdmin: user.bootstrapAdmin,
          foundationsComplete: payload.foundationsComplete,
        })
      ) {
        return;
      }
      closeGuide();
      setOpen(true);
      setExpanded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, mustChangePassword, autoTried, refreshReadiness, closeGuide]);

  /* Deep-link pause: reopen after route settles unless completed or « Plus tard ». */
  useEffect(() => {
    if (!reopenAfterNav) return;
    if (
      !user?.id ||
      isSetupDismissed(user.id) ||
      complete ||
      !canAccessInitialSetup({
        roles: user.roles,
        bootstrapAdmin: user.bootstrapAdmin,
        foundationsComplete: complete,
      })
    ) {
      setReopenAfterNav(false);
      return;
    }
    const timer = window.setTimeout(() => {
      closeGuide();
      setOpen(true);
      setExpanded(true);
      void refreshReadiness();
      setReopenAfterNav(false);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [
    location.pathname,
    reopenAfterNav,
    user?.id,
    user?.roles,
    user?.bootstrapAdmin,
    complete,
    closeGuide,
    refreshReadiness,
  ]);

  useEffect(() => {
    return subscribeCustomRoleCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingRoleCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingRoleCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('roles'));
        void refreshReadiness();
        void loadPresentRoles();
        return;
      }
      /* cancelled */
      setAwaitingRoleCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('roles'));
    });
  }, [closeGuide, refreshReadiness, loadPresentRoles]);

  useEffect(() => {
    return subscribeRolesBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingRoleImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingRoleImport(false);
        if (ev.createdCount > 0) setRolesImportDone(true);
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('roles'));
        void refreshReadiness();
        void loadPresentRoles();
        return;
      }
      setAwaitingRoleImport(false);
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('roles'));
    });
  }, [refreshReadiness, loadPresentRoles]);

  useEffect(() => {
    return subscribeStaffUserCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingStaffUser(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStaffUser(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('team'));
        void refreshReadiness();
        return;
      }
      setAwaitingStaffUser(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('team'));
    });
  }, [closeGuide, refreshReadiness]);

  useEffect(() => {
    return subscribeStaffUsersBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingStaffUsersImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStaffUsersImport(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('team'));
        void refreshReadiness();
        return;
      }
      setAwaitingStaffUsersImport(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('team'));
    });
  }, [closeGuide, refreshReadiness]);

  useEffect(() => {
    return subscribeMarkeCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingMarkeCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingMarkeCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('markes'));
        void refreshReadiness();
        void loadSetupMarkes();
        return;
      }
      setAwaitingMarkeCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('markes'));
    });
  }, [closeGuide, refreshReadiness, loadSetupMarkes]);

  useEffect(() => {
    return subscribeMarkeImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingMarkeImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingMarkeImport(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('markes'));
        void refreshReadiness();
        void loadSetupMarkes();
        return;
      }
      setAwaitingMarkeImport(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('markes'));
    });
  }, [closeGuide, refreshReadiness, loadSetupMarkes]);

  useEffect(() => {
    return subscribeStructureCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingStructureCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingStructureCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('structure'));
        void refreshReadiness();
        void loadSetupProductTypes();
        void loadSetupCategories();
        return;
      }
      setAwaitingStructureCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('structure'));
    });
  }, [closeGuide, refreshReadiness, loadSetupProductTypes, loadSetupCategories]);

  useEffect(() => {
    return subscribeProductTypesImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingProductTypesImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingProductTypesImport(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('structure'));
        void refreshReadiness();
        void loadSetupProductTypes();
        return;
      }
      setAwaitingProductTypesImport(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('structure'));
    });
  }, [closeGuide, refreshReadiness, loadSetupProductTypes]);

  useEffect(() => {
    return subscribeCategoriesImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingCategoriesImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingCategoriesImport(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('structure'));
        void refreshReadiness();
        void loadSetupCategories();
        return;
      }
      setAwaitingCategoriesImport(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('structure'));
    });
  }, [closeGuide, refreshReadiness, loadSetupCategories]);

  const accompanyPersonalizedRoleCreate = useCallback(() => {
    beginAwaitCustomRoleCreate();
    setExpanded(false);
    navigate('/roles?create=1');
  }, [navigate]);

  const accompanyRolesBulkImport = useCallback(() => {
    beginAwaitRolesBulkImport();
    setExpanded(false);
    navigate('/roles?import=1');
  }, [navigate]);

  const accompanyStaffUserCreate = useCallback(
    (roleName: string) => {
      beginAwaitStaffUserCreate(roleName);
      setExpanded(false);
      navigate(
        `${ADMIN_USERS.path}?create=1&role=${encodeURIComponent(roleName)}`,
      );
    },
    [navigate],
  );

  const accompanyStaffUsersImport = useCallback(() => {
    beginAwaitStaffUsersImport();
    setExpanded(false);
    navigate(`${ADMIN_USERS.path}?import=1`);
  }, [navigate]);

  const accompanyMarkeCreate = useCallback(() => {
    beginAwaitMarkeCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=marques&create=1');
  }, [navigate]);

  const accompanyMarkeImport = useCallback(() => {
    beginAwaitMarkeImport();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=marques&import=1');
  }, [navigate]);

  const accompanyCategoryCreate = useCallback(() => {
    beginAwaitStructureCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=structure&createStructure=1');
  }, [navigate]);

  const accompanyCategoriesImport = useCallback(() => {
    beginAwaitCategoriesImport();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=structure&import=1');
  }, [navigate]);

  const accompanyLocationCreate = useCallback(() => {
    beginAwaitLocationCreate();
    setExpanded(false);
    navigate('/inventory/locations?create=1');
  }, [navigate]);

  const accompanyProductDetailTypeCreate = useCallback(() => {
    beginAwaitProductDetailTypeCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=fiches&create=1');
  }, [navigate]);

  const accompanyOptionGroupCreate = useCallback(() => {
    beginAwaitOptionGroupCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=options&create=1');
  }, [navigate]);

  const accompanyTagFamilyCreate = useCallback(() => {
    beginAwaitTagFamilyCreate();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=tags&create=1');
  }, [navigate]);

  const accompanyTagsImport = useCallback(() => {
    beginAwaitTagsBulkImport();
    setExpanded(false);
    navigate('/catalog?tab=taxonomy&taxonomyTab=tags&import=1');
  }, [navigate]);

  useEffect(() => {
    return subscribeProductDetailTypeCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingProductDetailTypeCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingProductDetailTypeCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('fiches'));
        void refreshReadiness();
        void loadSetupProductDetailTypes();
        return;
      }
      setAwaitingProductDetailTypeCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('fiches'));
    });
  }, [closeGuide, refreshReadiness, loadSetupProductDetailTypes]);

  useEffect(() => {
    return subscribeOptionGroupCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingOptionGroupCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingOptionGroupCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('options'));
        void refreshReadiness();
        void loadSetupOptionGroups();
        return;
      }
      setAwaitingOptionGroupCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('options'));
    });
  }, [closeGuide, refreshReadiness, loadSetupOptionGroups]);

  useEffect(() => {
    return subscribeTagFamilyCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingTagFamilyCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingTagFamilyCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('tags'));
        void refreshReadiness();
        void loadSetupTagFamilies();
        return;
      }
      setAwaitingTagFamilyCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('tags'));
    });
  }, [closeGuide, refreshReadiness, loadSetupTagFamilies]);

  useEffect(() => {
    return subscribeTagsBulkImport((ev) => {
      if (ev.type === 'started') {
        setAwaitingTagsImport(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingTagsImport(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('tags'));
        void refreshReadiness();
        void loadSetupTagFamilies();
        return;
      }
      setAwaitingTagsImport(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('tags'));
    });
  }, [closeGuide, refreshReadiness, loadSetupTagFamilies]);

  useEffect(() => {
    return subscribeLocationCreate((ev) => {
      if (ev.type === 'started') {
        setAwaitingLocationCreate(true);
        setExpanded(false);
        return;
      }
      if (ev.type === 'completed') {
        setAwaitingLocationCreate(false);
        closeGuide();
        setOpen(true);
        setExpanded(true);
        setStepIndex(setupStepIndex('locations'));
        void refreshReadiness();
        void loadSetupLocations();
        return;
      }
      setAwaitingLocationCreate(false);
      closeGuide();
      setOpen(true);
      setExpanded(true);
      setStepIndex(setupStepIndex('locations'));
    });
  }, [closeGuide, refreshReadiness, loadSetupLocations]);

  type TeamMemberFace = { id: string; label: string; src: string | null };
  const [teamFacesByRole, setTeamFacesByRole] = useState<Record<string, TeamMemberFace[]>>(
    {},
  );
  const [facePreview, setFacePreview] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!open || !expanded) return;
    if (STEPS[stepIndex]?.id !== 'team') return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    void apiGetListSimple<UserRow>('/admin/users', token)
      .then((rows) => {
        if (cancelled) return;
        const next: Record<string, TeamMemberFace[]> = {};
        for (const u of rows) {
          if (!u.active || u.deletedAt) continue;
          const label = formatUserFullName(u)?.trim() || u.email;
          const face: TeamMemberFace = {
            id: u.id,
            label,
            src: u.profilePictureUrl ?? null,
          };
          for (const role of u.roles ?? []) {
            const list = next[role] ?? [];
            list.push(face);
            next[role] = list;
          }
        }
        setTeamFacesByRole(next);
      })
      .catch(() => {
        if (!cancelled) setTeamFacesByRole({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, expanded, stepIndex, foundations.team.activeStaffCount, foundations.team.slotsMet]);

  /**
   * Esc → collapse when incomplete; dismiss when done.
   * Backdrop same: collapse incomplete, dismiss complete.
   * While awaiting role create, Esc only collapses (already collapsed).
   */
  useEffect(() => {
    if (!open || !expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      if (!complete) {
        collapsePanel();
        return;
      }
      requestDismissForLater();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, complete, collapsePanel, requestDismissForLater]);

  const currentStep = STEPS[stepIndex];
  /** Soft preference steps stay after hard complete — do not jump to Terminé via `complete`. */
  const onDonePanel = stepIndex >= STEPS.length;

  const stepDone = useMemo(() => {
    return STEPS.map((s) => isFoundationStepComplete(s.id, foundations));
  }, [foundations]);

  /** Unmet roles first so empty slots stay in the 3-row window. */
  const teamSlotsOrdered = useMemo(() => {
    const slots = foundations.team.slots;
    return [...slots].sort((a, b) => Number(a.met) - Number(b.met));
  }, [foundations.team.slots]);

  const canContinueStep = onDonePanel ? true : stepDone[stepIndex] === true;

  const headerStepLabel = onDonePanel
    ? 'Terminé'
    : (currentStep?.label ?? STEPS[0]!.label);

  const progressLabel = `${pct}\u00a0%`;

  const headerAria = onDonePanel
    ? `Configuration initiale, ${progressLabel}, terminé`
    : `Configuration initiale, ${progressLabel}, étape ${headerStepLabel}`;

  const finishAndHandoff = () => {
    setSetupDismissed(user?.id, true);
    setReopenAfterNav(false);
    setOpen(false);
    setExpanded(false);
    navigate(ADMIN_PRODUCTS.path);
    void refreshReadiness().then((payload) => {
      if (TOURS_ENABLED) {
        const visible =
          payload?.visibleIds ?? guide.visiblePacks.map((p) => p.id);
        const packId = visible.includes('people')
          ? 'people'
          : (payload?.nextStepId ?? visible[0] ?? null);
        offerParcoursCollapsed({ packId });
        showToast(
          'Configuration terminée. Ouvrez Parcours en bas pour démarrer le guide métier.',
          { tone: 'info' },
        );
        return;
      }
      showToast('Configuration initiale terminée.', { tone: 'info' });
    });
  };

  if (!open || typeof document === 'undefined') return null;

  const sheet = (
    <div className={dockStyles.portalHost} data-wp-setup-wizard="">
      <div
        className={
          expanded ? `${dockStyles.backdrop} ${dockStyles.backdropVisible}` : dockStyles.backdrop
        }
        aria-hidden
        onMouseDown={(e) => {
          if (!expanded || e.target !== e.currentTarget) return;
          if (!complete) {
            collapsePanel();
            return;
          }
          requestDismissForLater();
        }}
      />
      <aside
        ref={sheetRef}
        className={
          expanded
            ? `${dockStyles.sheet} ${dockStyles.sheetExpanded}`
            : `${dockStyles.sheet} ${dockStyles.sheetCollapsed}`
        }
        data-wp-collapse-instant={collapseInstant ? '' : undefined}
        role="dialog"
        aria-modal={expanded ? true : undefined}
        aria-labelledby={titleId}
        aria-expanded={expanded}
      >
        <div className={styles.stripChrome}>
          <div className={`${dockStyles.strip} ${styles.stripRow}`} data-wp-dock-strip="">
            <button
              type="button"
              className={styles.stripToggle}
              onClick={() => (expanded ? collapsePanel() : expandPanel())}
              aria-label={
                expanded ? `${headerAria}. Réduire.` : `${headerAria}. Ouvrir.`
              }
            >
              <span className={styles.headerLead}>
                <span id={titleId} className={dockStyles.stripCopy}>
                  Configuration initiale
                </span>
                {!expanded ? (
                  <span className={styles.headerStepHint}>
                    {awaitingRoleImport
                      ? 'Import de rôles…'
                      : awaitingRoleCreate
                        ? 'Création d’un rôle…'
                        : awaitingStaffUsersImport
                          ? 'Import de comptes…'
                          : awaitingStaffUser
                            ? 'Création d’un compte…'
                            : awaitingMarkeImport
                              ? 'Import de marques…'
                              : awaitingMarkeCreate
                                ? 'Création d’une marque…'
                                : awaitingProductTypesImport
                                    ? 'Import de types…'
                                    : awaitingStructureCreate
                                      ? 'Création d’une structure…'
                                      : awaitingCategoriesImport
                                      ? 'Import de catégories…'
                                      : awaitingProductDetailTypeCreate
                                        ? 'Création d’un type de détail…'
                                        : awaitingOptionGroupCreate
                                          ? 'Création d’un groupe d’options…'
                                          : awaitingTagFamilyCreate
                                            ? 'Création d’une famille de tags…'
                                            : awaitingTagsImport
                                              ? 'Import de tags…'
                                              : awaitingLocationCreate
                                                ? 'Création d’un emplacement…'
                                                : headerStepLabel}
                  </span>
                ) : null}
              </span>
              <span className={dockStyles.stripEnd}>
                <span className={styles.progressPct}>{progressLabel}</span>
                <span
                  className={
                    expanded
                      ? `${dockStyles.pillTrack} ${dockStyles.pillTrackWide}`
                      : dockStyles.pillTrack
                  }
                  aria-hidden
                >
                  <span className={dockStyles.pillFill} style={{ width: `${pct}%` }} />
                </span>
                <IconChevronRight
                  size={18}
                  stroke={1.6}
                  aria-hidden
                  className={
                    expanded
                      ? `${dockStyles.stripChevron} ${dockStyles.stripChevronExpanded}`
                      : dockStyles.stripChevron
                  }
                />
              </span>
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              borderless
              className={styles.stripDismiss}
              aria-label="Plus tard"
              title="Plus tard"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                requestDismissForLater();
              }}
            >
              <IconClock size={18} stroke={1.6} aria-hidden />
            </IconButton>
          </div>
          {expanded ? (
            <ProximityScope axis="x" className={styles.stepTabs} aria-label="Étapes">
              {STEPS.map((s, i) => {
                const done = stepDone[i];
                const active = !onDonePanel && i === stepIndex;
                const canGo = onDonePanel || i < stepIndex;
                const className = [
                  styles.stepTab,
                  active ? styles.stepTabActive : '',
                  done ? styles.stepTabDone : '',
                  canGo ? styles.stepTabNav : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const label = `${i + 1}. ${s.label}`;
                if (canGo) {
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-wp-proximity-item=""
                      className={className}
                      aria-current={active ? 'step' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStepIndex(i);
                      }}
                    >
                      {label}
                    </button>
                  );
                }
                return (
                  <span
                    key={s.id}
                    data-wp-proximity-item=""
                    className={className}
                    aria-current={active ? 'step' : undefined}
                  >
                    {label}
                  </span>
                );
              })}
              <span
                data-wp-proximity-item=""
                className={[
                  styles.stepTab,
                  styles.stepTabDone,
                  onDonePanel ? styles.stepTabActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={onDonePanel ? 'step' : undefined}
              >
                {STEPS.length + 1}. Terminé
              </span>
            </ProximityScope>
          ) : null}
        </div>

        <div className={dockStyles.expandSlot} aria-hidden={!expanded}>
          <div className={dockStyles.expandInner} {...(!expanded ? { inert: true } : {})}>
            <ScrollArea
              id="setup-wizard-drawer"
              className={[
                dockStyles.body,
                !onDonePanel &&
                (currentStep?.id === 'roles' ||
                  currentStep?.id === 'team' ||
                  currentStep?.id === 'markes' ||
                  currentStep?.id === 'structure' ||
                  currentStep?.id === 'locations')
                  ? styles.bodyFill
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              fade="y"
              maxHeight="100%"
              data-tour="setup-wizard-drawer"
            >
              <StepTravel
                index={onDonePanel ? STEPS.length : stepIndex}
                fill={
                  !onDonePanel &&
                  (currentStep?.id === 'roles' ||
                    currentStep?.id === 'team' ||
                    currentStep?.id === 'markes' ||
                    currentStep?.id === 'structure' ||
                    currentStep?.id === 'locations')
                }
              >
              {onDonePanel ? (
                <section className={`${styles.stepBlock} ${styles.successBlock}`}>
                  <div className={styles.successMark} aria-hidden>
                    <IconCircleCheck size={40} stroke={1.5} />
                  </div>
                  <h2 className={styles.successTitle}>Configuration réussie</h2>
                  <Text size="sm" c="dimmed" className={styles.successLede}>
                    Les fondations de la plateforme sont en place. Vous pouvez fermer et
                    commencer à travailler dans l’admin.
                  </Text>
                  <ul className={styles.successList}>
                    {STEPS.filter(
                      (s) =>
                        s.id !== 'org_identity' &&
                        s.id !== 'pref_low_stock' &&
                        s.id !== 'pref_near_expiry',
                    ).map((s) => (
                      <li key={s.id} className={styles.successListItem}>
                        <IconCheck size={14} stroke={2.2} aria-hidden />
                        <span>{s.label}</span>
                      </li>
                    ))}
                  </ul>
                  {TOURS_ENABLED ? (
                    <Text size="xs" c="dimmed" className={styles.successHint}>
                      Après « Terminer », le bandeau Parcours en bas peut lancer le guide
                      métier.
                    </Text>
                  ) : null}
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'org_identity' ? (
                <section className={styles.stepBlock}>
                  <h2 className={styles.stepTitle}>Société</h2>
                  <p className={styles.stepLede}>
                    Identité légale de WaPharma pour les conventions (optionnel — modifiable ensuite
                    dans Paramètres → Société).
                  </p>
                  <OrganizationLegalIdentityForm
                    ref={orgIdentityRef}
                    hideSaveButton
                    quietToast
                  />
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'roles' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.rolePaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Rôles système</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Modèles de rôles"
                      >
                        {SEEDED_ROLE_NAMES.map((name) => {
                          const present = presentRoleNames.has(name);
                          const label = userRoleLabelFr(name);
                          return (
                            <span
                              key={name}
                              role="listitem"
                              className={[
                                styles.roleTag,
                                present ? styles.roleTagPresent : styles.roleTagPending,
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <img
                                className={styles.roleTagFace}
                                src={rolePersonaDataUri(name)}
                                alt=""
                                width={28}
                                height={28}
                                decoding="async"
                              />
                              <span className={styles.roleTagLabel}>{label}</span>
                              {present ? (
                                <span
                                  className={styles.roleTagCheck}
                                  aria-label={`${label} — installé`}
                                  title="Installé"
                                >
                                  <IconCheck size={14} stroke={2.2} aria-hidden />
                                </span>
                              ) : null}
                              <Tooltip
                                wide
                                portal
                                position="bottom"
                                label={<RoleCapabilityTip roleName={name} />}
                              >
                                <button
                                  type="button"
                                  className={styles.roleTagInfo}
                                  tabIndex={expanded ? undefined : -1}
                                  aria-label={`Capacités : ${label}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                </button>
                              </Tooltip>
                            </span>
                          );
                        })}
                      </RoleTagGridShell>
                      {roleSeedErr ? <InlineError>{roleSeedErr}</InlineError> : null}
                      {missingRoleTemplates.length > 0 ||
                      foundations.seededRoleCount < foundations.seededRoleExpected ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={roleSeedBusy}
                            onClick={() => void installRoleTemplates()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {roleSeedBusy ? 'Installation…' : 'Installer les rôles'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Nouveau rôle</h3>
                      {customRoles.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Rôles personnalisés"
                        >
                          {customRoles.map((role) => {
                            const label = userRoleLabelFr(role.name);
                            return (
                              <span
                                key={role.id}
                                role="listitem"
                                className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              >
                                <img
                                  className={styles.roleTagFace}
                                  src={rolePersonaDataUri(role.name)}
                                  alt=""
                                  width={28}
                                  height={28}
                                  decoding="async"
                                />
                                <span className={styles.roleTagLabel}>{label}</span>
                                <span
                                  className={styles.roleTagCheck}
                                  aria-label={`${label} — créé`}
                                  title="Créé"
                                >
                                  <IconCheck size={14} stroke={2.2} aria-hidden />
                                </span>
                                <Tooltip
                                  wide
                                  portal
                                  position="bottom"
                                  label={
                                    <RoleCapabilityTip
                                      roleName={role.name}
                                      packs={role.packs}
                                      grants={role.grants}
                                    />
                                  }
                                >
                                  <button
                                    type="button"
                                    className={styles.roleTagInfo}
                                    tabIndex={expanded ? undefined : -1}
                                    aria-label={`Capacités : ${label}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                  </button>
                                </Tooltip>
                              </span>
                            );
                          })}
                        </RoleTagGridShell>
                      ) : (
                        <div className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`} aria-hidden>
                          <SetupWizardPathArt kind="create-role" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyPersonalizedRoleCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconUserPlus size={16} stroke={1.5} aria-hidden />
                            Créer un rôle
                          </span>
                        </Button>
                      </div>
                    </div>

                    <div
                      className={[
                        styles.rolePath,
                        styles.rolePathCustom,
                        rolesImportDone ? styles.rolePathDisabled : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-disabled={rolesImportDone || undefined}
                    >
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title={
                          rolesImportDone
                            ? 'Import effectué'
                            : 'Import en masse depuis un fichier'
                        }
                      >
                        {rolesImportDone ? (
                          <span className={`${styles.roleImportGlyph} ${styles.roleImportGlyphDone}`}>
                            <IconCheck size={28} stroke={2} aria-hidden />
                          </span>
                        ) : (
                          <SetupWizardPathArt kind="import" />
                        )}
                      </div>
                      {!rolesImportDone ? (
                        <div className={styles.rolePathActions}>
                          <div className={styles.rolePathActionRow}>
                            <Button
                              type="button"
                              size="sm"
                              variant="soft"
                              tabIndex={expanded ? undefined : -1}
                              onClick={accompanyRolesBulkImport}
                            >
                              <span className={styles.templateBtnLabel}>
                                <IconPackageImport size={16} stroke={1.5} aria-hidden />
                                Importer un fichier
                              </span>
                            </Button>
                            <SetupWizardTemplateMenu
                              tabIndex={expanded ? undefined : -1}
                              onDownload={(format) => {
                                const token = getToken();
                                if (token) void downloadRolesImportTemplate(token, format);
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'team' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer un compte</h3>
                      <TeamRoleListShell
                        className={styles.teamRoleListScroll}
                        aria-label="Comptes par rôle"
                      >
                        <div className={styles.teamRoleList}>
                          {teamSlotsOrdered.map((slot) => {
                            const faces = teamFacesByRole[slot.id] ?? [];
                            const label = userRoleLabelFr(slot.id);
                            const needsStaff = !slot.met;
                            return (
                              <div
                                key={slot.id}
                                className={[
                                  styles.teamRoleRow,
                                  needsStaff ? styles.teamRoleRowUnmet : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                aria-invalid={needsStaff || undefined}
                              >
                                <div className={styles.teamRoleMain}>
                                  <UserRolePill role={slot.id} faceShape="tile" />
                                  <div
                                    className={styles.teamFaceStack}
                                    aria-label={`Comptes — ${label}`}
                                  >
                                    {faces.slice(0, 4).map((face) => (
                                      <span
                                        key={face.id}
                                        className={styles.teamMemberChip}
                                        title={face.label}
                                      >
                                        {face.src ? (
                                          <button
                                            type="button"
                                            className={styles.teamFaceZoom}
                                            tabIndex={expanded ? undefined : -1}
                                            aria-label={`Agrandir la photo de ${face.label}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setFacePreview({ src: face.src!, alt: face.label });
                                            }}
                                          >
                                            <Avatar
                                              size="sm"
                                              label={face.label}
                                              src={face.src}
                                              className={styles.teamFace}
                                            />
                                          </button>
                                        ) : (
                                          <Avatar
                                            size="sm"
                                            label={face.label}
                                            src={face.src}
                                            className={styles.teamFace}
                                          />
                                        )}
                                        <span className={styles.teamMemberName}>{face.label}</span>
                                      </span>
                                    ))}
                                    {faces.length > 4 ? (
                                      <span className={styles.teamFaceMore}>
                                        +{faces.length - 4}
                                      </span>
                                    ) : null}
                                    {slot.met ? (
                                      <span
                                        className={styles.teamRoleCheck}
                                        aria-label={`${label} — pourvu`}
                                        title="Pourvu"
                                      >
                                        <IconCheck size={14} stroke={2.2} aria-hidden />
                                      </span>
                                    ) : (
                                      <span className={styles.teamRoleUnmetHint} aria-live="polite">
                                        Aucun compte
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <IconButton
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  aria-label={`Créer un compte pour ${label}`}
                                  tabIndex={expanded ? undefined : -1}
                                  onClick={() => accompanyStaffUserCreate(slot.id)}
                                >
                                  <IconPlus {...WP_ICON_NAV} />
                                </IconButton>
                              </div>
                            );
                          })}
                        </div>
                      </TeamRoleListShell>
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import en masse depuis un fichier"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <div className={styles.rolePathActionRow}>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyStaffUsersImport}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconPackageImport size={16} stroke={1.5} aria-hidden />
                              Importer des comptes
                            </span>
                          </Button>
                          <SetupWizardTemplateMenu
                            tabIndex={expanded ? undefined : -1}
                            onDownload={(format) => {
                              const token = getToken();
                              if (token) void downloadUsersImportTemplate(token, format);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {foundations.team.slotsMet && !foundations.team.staffFloorMet ? (
                    <InlineError>
                      {foundations.team.activeStaffCount}/2 comptes — créez-en un autre.
                    </InlineError>
                  ) : null}
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'markes' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer une marque</h3>
                      {setupMarkes.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Marques créées"
                        >
                          {setupMarkes.map((m) => (
                            <span
                              key={m.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                            >
                              <MarkeLogoThumb url={m.logoUrl} name={m.name} size="sm" />
                              <span className={styles.roleTagLabel}>{m.name}</span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — créée`}
                                title="Créée"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            </span>
                          ))}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer une marque"
                        >
                          <SetupWizardPathArt kind="brand" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyMarkeCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPlus size={16} stroke={1.5} aria-hidden />
                            Nouvelle marque
                          </span>
                        </Button>
                      </div>
                    </div>
                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import de marques"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <div className={styles.rolePathActionRow}>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyMarkeImport}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconPackageImport size={16} stroke={1.5} aria-hidden />
                              Importer un fichier
                            </span>
                          </Button>
                          <SetupWizardTemplateMenu
                            tabIndex={expanded ? undefined : -1}
                            onDownload={(format) => downloadFacetTemplate('markes', format)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'structure' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={`${styles.rolePaths} ${styles.structurePaths}`}>
                    <div className={`${styles.rolePath} ${styles.structureGraphPanel}`}>
                      <h3 className={styles.rolePathTitle}>Types & catégories</h3>
                      <div
                        className={cx(
                          styles.taxonomyGraphShell,
                          expanded && styles.taxonomyGraphShellExpanded,
                        )}
                      >
                        {(taxonomySeedModels?.preview.productTypes.length ?? 0) > 0 ? (
                          <ProductTypeTaxonomyGraph
                            compact
                            productTypes={taxonomySeedModels!.preview.productTypes}
                            categories={taxonomySeedModels!.preview.categories}
                            selectedTypeCode={seedPreviewTypeCode}
                            canEdit={false}
                            token={getToken() ?? ''}
                            onChange={loadTaxonomySeedModels}
                            onSelectType={setSeedPreviewTypeCode}
                            onCycleSeedPhoto={cycleTaxonomySeedPhoto}
                          />
                        ) : (
                          <div
                            className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                            title="Modèles taxonomy"
                          >
                            <SetupWizardPathArt kind="structure" />
                          </div>
                        )}
                      </div>
                      {taxonomySeedErr ? <InlineError>{taxonomySeedErr}</InlineError> : null}
                      {!taxonomySeedModels?.complete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={taxonomySeedBusy}
                            onClick={() => void installTaxonomySeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {taxonomySeedBusy ? 'Installation…' : 'Installer types & catégories'}
                            </span>
                          </Button>
                        </div>
                      ) : (
                        <p className={styles.taxonomySeedDoneHint}>Types & catégories installés</p>
                      )}
                    </div>

                    <div className={`${styles.rolePath} ${styles.structureGraphPanel}`}>
                      <h3 className={styles.rolePathTitle}>Structure</h3>
                      <div
                        className={cx(
                          styles.taxonomyGraphShell,
                          expanded && styles.taxonomyGraphShellExpanded,
                        )}
                      >
                        {hasSetupTaxonomyTree ? (
                          <ProductTypeTaxonomyGraph
                            compact
                            productTypes={setupSortedProductTypes}
                            categories={setupCategories}
                            selectedTypeCode={setupSelectedTypeCode}
                            canEdit={false}
                            token={getToken() ?? ''}
                            onChange={reloadSetupTaxonomy}
                            onSelectType={setSetupSelectedTypeCode}
                          />
                        ) : (
                          <div
                            className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter} ${styles.structureDropHint}`}
                            title="Créer une structure"
                          >
                            <SetupWizardPathArt kind="structure" />
                            <p className={styles.structureDropHintText}>
                              Aucune structure installée. Utilisez « Installer types & catégories » ou
                              créez-en une.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyCategoryCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconPlus size={16} stroke={1.5} aria-hidden />
                            Nouvelle structure
                          </span>
                        </Button>
                        <div className={styles.rolePathActionRow}>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyCategoriesImport}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconPackageImport size={16} stroke={1.5} aria-hidden />
                              Importer
                            </span>
                          </Button>
                          <SetupWizardTemplateMenu
                            tabIndex={expanded ? undefined : -1}
                            onDownload={() => downloadStructureTemplate()}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'fiches' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <p className={styles.stepLede}>
                    Même contenu pour toutes les variantes du produit (ingrédients, précautions…).
                  </p>
                  <div className={styles.teamPaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Types de fiche</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Types de détail modèles"
                      >
                        {ficheSeedModels.map((m) => {
                          const KindIcon = productDetailKindIcon(m.kind);
                          const kindLabel = productDetailKindShortLabel(m.kind);
                          return (
                          <span
                            key={m.code}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span
                              className={styles.roleTagKind}
                              aria-label={`Saisie : ${kindLabel}`}
                              title={kindLabel}
                            >
                              <KindIcon size={14} stroke={1.7} aria-hidden />
                            </span>
                            <span className={styles.roleTagLabel}>{m.name}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — installé`}
                                title="Installé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                            <Tooltip
                              wide
                              portal
                              position="bottom"
                              label={
                                <ProductDetailTypeExamplesTip
                                  name={m.name}
                                  code={m.code}
                                  kind={m.kind}
                                />
                              }
                            >
                              <button
                                type="button"
                                className={styles.roleTagInfo}
                                tabIndex={expanded ? undefined : -1}
                                aria-label={`Exemples de valeurs : ${m.name}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                              </button>
                            </Tooltip>
                          </span>
                          );
                        })}                      </RoleTagGridShell>
                      {ficheSeedErr ? <InlineError>{ficheSeedErr}</InlineError> : null}
                      {!ficheSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={ficheSeedBusy}
                            onClick={() => void installFicheSeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {ficheSeedBusy ? 'Installation…' : 'Installer les types de fiche'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer un type de fiche</h3>
                      {setupProductDetailTypes.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Types de détail créés"
                        >
                          {setupProductDetailTypes.map((t) => {
                            const KindIcon = productDetailKindIcon(t.kind);
                            const kindLabel = productDetailKindShortLabel(t.kind);
                            return (
                            <span
                              key={t.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                            >
                              <span
                                className={styles.roleTagKind}
                                aria-label={`Saisie : ${kindLabel}`}
                                title={kindLabel}
                              >
                                <KindIcon size={14} stroke={1.7} aria-hidden />
                              </span>
                              <span className={styles.roleTagLabel}>{t.name}</span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${t.name} — créé`}
                                title="Créé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                              <Tooltip
                                wide
                                portal
                                position="bottom"
                                label={
                                  <ProductDetailTypeExamplesTip
                                    name={t.name}
                                    code={t.code}
                                    kind={t.kind}
                                  />
                                }
                              >
                                <button
                                  type="button"
                                  className={styles.roleTagInfo}
                                  tabIndex={expanded ? undefined : -1}
                                  aria-label={`Exemples de valeurs : ${t.name}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                </button>
                              </Tooltip>
                            </span>
                            );
                          })}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer un type de détail"
                        >
                          <SetupWizardPathArt kind="details" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyProductDetailTypeCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconListDetails size={16} stroke={1.5} aria-hidden />
                            Nouveau type
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'options' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <p className={styles.stepLede}>
                    Axes de variantes — chaque combinaison devient une référence (contenance, format…).
                  </p>
                  <div className={styles.teamPaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Axes modèles</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Groupes d’options modèles"
                      >
                        {optionSeedModels.map((m) => (
                          <span
                            key={m.code}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span className={styles.roleTagLabel}>{m.name}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — installé`}
                                title="Installé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                            <Tooltip
                              wide
                              portal
                              position="bottom"
                              label={
                                <OptionGroupExamplesTip
                                  name={m.name}
                                  blurb="Valeurs installées avec le modèle — une par référence variante."
                                  examples={m.valueLabels}
                                />
                              }
                            >
                              <button
                                type="button"
                                className={styles.roleTagInfo}
                                tabIndex={expanded ? undefined : -1}
                                aria-label={`Exemples de valeurs : ${m.name}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                              </button>
                            </Tooltip>
                          </span>
                        ))}
                      </RoleTagGridShell>
                      {optionSeedErr ? <InlineError>{optionSeedErr}</InlineError> : null}
                      {!optionSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={optionSeedBusy}
                            onClick={() => void installOptionSeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {optionSeedBusy ? 'Installation…' : 'Installer les axes'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer un axe</h3>
                      {setupOptionGroups.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Groupes d’options créés"
                        >
                          {setupOptionGroups.map((g) => {
                            const seed = CANONICAL_OPTION_GROUPS.find((m) => m.code === g.code);
                            const examples = seed?.values.map((v) => v.label) ?? [];
                            return (
                            <span
                              key={g.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                            >
                              <span className={styles.roleTagLabel}>
                                {g.name}
                                {g.valueCount > 0 ? ` (${g.valueCount})` : ''}
                              </span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${g.name} — créé`}
                                title="Créé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                              <Tooltip
                                wide
                                portal
                                position="bottom"
                                label={
                                  <OptionGroupExamplesTip
                                    name={g.name}
                                    blurb={
                                      examples.length > 0
                                        ? 'Valeurs du modèle (échantillon).'
                                        : `Axe personnalisé — ${g.valueCount} valeur${g.valueCount === 1 ? '' : 's'}.`
                                    }
                                    examples={examples}
                                  />
                                }
                              >
                                <button
                                  type="button"
                                  className={styles.roleTagInfo}
                                  tabIndex={expanded ? undefined : -1}
                                  aria-label={`Exemples de valeurs : ${g.name}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                </button>
                              </Tooltip>
                            </span>
                            );
                          })}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer un groupe d’options"
                        >
                          <SetupWizardPathArt kind="options" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyOptionGroupCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconAdjustmentsHorizontal size={16} stroke={1.5} aria-hidden />
                            Nouveau groupe
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'tags' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <p className={styles.stepLede}>
                    Vocabulaire produit — étapes routine, type de peau, préoccupations, textures
                    (regimen / quiz).
                  </p>
                  <div className={styles.rolePaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Familles modèles</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Familles de tags modèles"
                      >
                        {tagSeedModels.map((m) => (
                          <span
                            key={m.code}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span className={styles.roleTagLabel}>{m.name}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.name} — installé`}
                                title="Installé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                            <Tooltip
                              wide
                              portal
                              position="bottom"
                              label={
                                <OptionGroupExamplesTip
                                  name={m.name}
                                  blurb="Tags installés avec le modèle — une valeur par famille sur le produit."
                                  examples={m.valueLabels}
                                />
                              }
                            >
                              <button
                                type="button"
                                className={styles.roleTagInfo}
                                tabIndex={expanded ? undefined : -1}
                                aria-label={`Exemples de tags : ${m.name}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                              </button>
                            </Tooltip>
                          </span>
                        ))}
                      </RoleTagGridShell>
                      {tagSeedErr ? <InlineError>{tagSeedErr}</InlineError> : null}
                      {!tagSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={tagSeedBusy}
                            onClick={() => void installTagSeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {tagSeedBusy ? 'Installation…' : 'Installer les tags'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer une famille</h3>
                      {setupTagFamilies.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Familles de tags créées"
                        >
                          {setupTagFamilies.map((g) => {
                            const seed = CANONICAL_PRODUCT_TAG_GROUPS.find((m) => m.code === g.code);
                            const examples = seed?.tags.map((t) => t.label) ?? [];
                            return (
                              <span
                                key={g.id}
                                role="listitem"
                                className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              >
                                <span className={styles.roleTagLabel}>
                                  {g.name}
                                  {g.tagCount > 0 ? ` (${g.tagCount})` : ''}
                                </span>
                                <span
                                  className={styles.roleTagCheck}
                                  aria-label={`${g.name} — créé`}
                                  title="Créé"
                                >
                                  <IconCheck size={14} stroke={2.2} aria-hidden />
                                </span>
                                <Tooltip
                                  wide
                                  portal
                                  position="bottom"
                                  label={
                                    <OptionGroupExamplesTip
                                      name={g.name}
                                      blurb={
                                        examples.length > 0
                                          ? 'Tags du modèle (échantillon).'
                                          : `Famille personnalisée — ${g.tagCount} tag${g.tagCount === 1 ? '' : 's'}.`
                                      }
                                      examples={examples}
                                    />
                                  }
                                >
                                  <button
                                    type="button"
                                    className={styles.roleTagInfo}
                                    tabIndex={expanded ? undefined : -1}
                                    aria-label={`Exemples de tags : ${g.name}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <IconInfoCircle size={15} stroke={1.7} aria-hidden />
                                  </button>
                                </Tooltip>
                              </span>
                            );
                          })}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer une famille de tags"
                        >
                          <SetupWizardPathArt kind="options" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyTagFamilyCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconTags size={16} stroke={1.5} aria-hidden />
                            Nouvelle famille
                          </span>
                        </Button>
                      </div>
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Importer</h3>
                      <div
                        className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                        aria-hidden
                        title="Import en masse depuis un fichier"
                      >
                        <SetupWizardPathArt kind="import" />
                      </div>
                      <div className={styles.rolePathActions}>
                        <div className={styles.rolePathActionRow}>
                          <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            tabIndex={expanded ? undefined : -1}
                            onClick={accompanyTagsImport}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconPackageImport size={16} stroke={1.5} aria-hidden />
                              Importer des tags
                            </span>
                          </Button>
                          <SetupWizardTemplateMenu
                            tabIndex={expanded ? undefined : -1}
                            onDownload={(format) => {
                              const token = getToken();
                              if (token) void downloadTaxonomyFacetImportTemplate(token, 'tags', format);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'locations' ? (
                <section className={`${styles.stepBlock} ${styles.stepBlockFill}`}>
                  <div className={styles.teamPaths}>
                    <div className={styles.rolePath}>
                      <h3 className={styles.rolePathTitle}>Emplacements modèles</h3>
                      <RoleTagGridShell
                        className={styles.roleTagGrid}
                        aria-label="Emplacements modèles"
                      >
                        {locationSeedModels.map((m) => (
                          <span
                            key={m.type}
                            role="listitem"
                            className={[
                              styles.roleTag,
                              m.present ? styles.roleTagPresent : styles.roleTagPending,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            title={m.name}
                          >
                            <span className={styles.roleTagLabel}>{m.label}</span>
                            {m.present ? (
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${m.label} — installé`}
                                title="Installé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </RoleTagGridShell>
                      {locationSeedErr ? <InlineError>{locationSeedErr}</InlineError> : null}
                      {!locationSeedComplete ? (
                        <div className={styles.rolePathActions}>
                          <Button
                            type="button"
                            size="sm"
                            tabIndex={expanded ? undefined : -1}
                            disabled={locationSeedBusy}
                            onClick={() => void installLocationSeedModels()}
                          >
                            <span className={styles.templateBtnLabel}>
                              <IconDownload size={16} stroke={1.5} aria-hidden />
                              {locationSeedBusy ? 'Installation…' : 'Installer les emplacements'}
                            </span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <div className={`${styles.rolePath} ${styles.rolePathCustom}`}>
                      <h3 className={styles.rolePathTitle}>Créer un emplacement</h3>
                      {setupLocations.length > 0 ? (
                        <RoleTagGridShell
                          className={styles.roleTagGrid}
                          aria-label="Emplacements créés"
                        >
                          {setupLocations.map((loc) => (
                            <span
                              key={loc.id}
                              role="listitem"
                              className={`${styles.roleTag} ${styles.roleTagPresent}`}
                              title={loc.type ?? undefined}
                            >
                              <span className={styles.roleTagLabel}>{loc.name}</span>
                              <span
                                className={styles.roleTagCheck}
                                aria-label={`${loc.name} — créé`}
                                title="Créé"
                              >
                                <IconCheck size={14} stroke={2.2} aria-hidden />
                              </span>
                            </span>
                          ))}
                        </RoleTagGridShell>
                      ) : (
                        <div
                          className={`${styles.rolePathPlaceholder} ${styles.rolePathPlaceholderCenter}`}
                          aria-hidden
                          title="Créer un emplacement"
                        >
                          <SetupWizardPathArt kind="location" />
                        </div>
                      )}
                      <div className={styles.rolePathActions}>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          tabIndex={expanded ? undefined : -1}
                          onClick={accompanyLocationCreate}
                        >
                          <span className={styles.templateBtnLabel}>
                            <IconBuildingWarehouse size={16} stroke={1.5} aria-hidden />
                            Nouvel emplacement
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'pref_low_stock' ? (
                <section className={styles.stepBlock}>
                  <h2 className={styles.stepTitle}>Stock bas</h2>
                  <p className={styles.stepLede}>
                    Seuil d’alerte lorsque le stock disponible tombe sous ce niveau (optionnel —
                    vous pourrez le modifier plus tard).
                  </p>
                  <StockAlertsSettingsSection
                    ref={stockBasRef}
                    facet="lowStock"
                    showTitle={false}
                    hideSaveButton
                    quietToast
                  />
                </section>
              ) : null}

              {!onDonePanel && currentStep?.id === 'pref_near_expiry' ? (
                <section className={styles.stepBlock}>
                  <h2 className={styles.stepTitle}>Péremption</h2>
                  <p className={styles.stepLede}>
                    Nombre de jours avant péremption pour signaler un lot (optionnel — modifiable
                    ensuite dans les paramètres).
                  </p>
                  <StockAlertsSettingsSection
                    ref={peremptionRef}
                    facet="nearExpiry"
                    showTitle={false}
                    hideSaveButton
                    quietToast
                  />
                </section>
              ) : null}
              </StepTravel>
            </ScrollArea>

            <div className={dockStyles.footer}>
              <div className={`${dockStyles.footerInner} ${styles.footerRow}`}>
              {onDonePanel || stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="soft"
                  tabIndex={expanded ? undefined : -1}
                  onClick={() => {
                    if (onDonePanel) {
                      setStepIndex(STEPS.length - 1);
                      return;
                    }
                    setStepIndex((i) => Math.max(0, i - 1));
                  }}
                >
                  Retour
                </Button>
              ) : (
                <span />
              )}
              <div className={styles.footerEnd}>
                {onDonePanel ? (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    onClick={finishAndHandoff}
                  >
                    Terminer
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    disabled={!canContinueStep}
                    onClick={() => {
                      void (async () => {
                        const stepId = STEPS[stepIndex]?.id;
                        if (stepId === 'org_identity') {
                          const ok = await orgIdentityRef.current?.save();
                          if (ok === false) return;
                        } else if (stepId === 'pref_low_stock') {
                          const ok = await stockBasRef.current?.save();
                          if (ok === false) return;
                        } else if (stepId === 'pref_near_expiry') {
                          const ok = await peremptionRef.current?.save();
                          if (ok === false) return;
                        }
                        void refresh();
                        // After emplacements (fondations dures), land on bilan succès.
                        // Soft prefs stay reachable via the step tabs.
                        if (stepId === 'locations') {
                          setStepIndex(STEPS.length);
                          return;
                        }
                        setStepIndex((i) => i + 1);
                      })();
                    }}
                  >
                    {STEPS[stepIndex]?.id === 'pref_near_expiry'
                      ? 'Voir le bilan'
                      : STEPS[stepIndex]?.id === 'locations'
                        ? 'Terminer'
                        : 'Continuer'}
                  </Button>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );

  return (
    <>
      {createPortal(sheet, document.body)}
      <Modal
        opened={dismissConfirmOpen}
        onClose={() => setDismissConfirmOpen(false)}
        title="Plus tard ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Rester"
            confirmLabel="Plus tard"
            onCancel={() => setDismissConfirmOpen(false)}
            onConfirm={dismissForLater}
          />
        }
      >
        <Text size="sm">
          La configuration initiale restera accessible. Vous pourrez la reprendre quand vous voulez.
        </Text>
      </Modal>
      <Modal
        opened={facePreview !== null}
        onClose={() => setFacePreview(null)}
        title={facePreview?.alt ?? 'Photo'}
        size="md"
      >
        {facePreview ? (
          <img
            src={facePreview.src}
            alt={facePreview.alt}
            className={styles.facePreviewImage}
          />
        ) : null}
      </Modal>
    </>
  );
}
